import { Layout, Text, TextLink } from '@oneaudi/unified-web-alpha-components';
import { useAtomValue } from 'jotai';
import { useCallback } from 'react';
import { FinancingDisclaimerDialog } from '~/app/compositions/content/calculation-info/components/financing-disclaimer-dialog';
import { FsagMonthlyPriceRateDialog } from '~/app/compositions/content/calculation-info/components/fsag-finance-dialog';
import {
	CalculationInfoStyle,
	PriceBarTotalPrice,
	TotalPrice,
} from '~/app/compositions/content/calculation-info/styles/calculation-info-style';
import { useAppServices, useAppState } from '~/app/contexts/app-context';
import { useDialogs } from '~/app/hooks/use-dialogs';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { PriceDetailsLegalCanadaDialog } from '~/app/pages/summary/dialogs/price-detail';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction, LayerType } from '~/app/tracking/feld-m-types';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import { useBreakPoint } from '~/hooks/responsive/useBreakPoint';
import { useCurrencyFormatter } from '~/hooks/use-currency-formatter';
import { BasicProps } from '~/models/basic-props';
import { state } from '~/storage';
import { DATA_TEST_ID } from '~/test/data-test-id';

export function CalculationInfoLegalCanada({ className }: BasicProps) {
	const { config } = useMarketConfig();
	const { loadingFlags, catalog } = useConfigurator();
	const { formatPriceWithVAT } = useCurrencyFormatter();
	const showLoadingIndicator = loadingFlags.priceSummary;
	const currentView = useAtomValue(state.currentView);
	const { i18n } = useI18n();
	const totalPrice = catalog.priceSummary?.totalPrice;
	const { financingData } = useAppState();
	const { isXlBreakpoint } = useBreakPoint();

	const apr = financingData?.financingDetails?.detailGroups
		?.find((groups) => groups?.key === 'Price Details')
		?.items?.find((payment) => payment?.key === 'APR');
	const cash = financingData?.financingDetails?.detailGroups
		?.find((groups) => groups?.key === 'Price Details')
		?.items?.find((payment) => payment?.key === 'EstimatedPrice');
	const rate = financingData?.financingDetails?.detailGroups
		?.find((groups) => groups?.key === 'Price Details')
		?.items?.find((payment) => payment?.key === 'Rate');
	const currentProduct = financingData?.financing?.meta?.externalRequestOptions?.Product?.['@ID'];

	const {
		featureServices: { footnoteService },
	} = useAppServices();
	footnoteService?.getData();

	const { openDialog, isDialogOpen, closeDialog } = useDialogs();

	function scrollToBottom() {
		window?.scrollTo({ left: 0, top: document?.body.scrollHeight, behavior: 'smooth' });
	}

	const trackClickEvent = useCallback((eventName: LayerType, label: string) => {
		appTrackingService.summaryClickEvent({
			eventAction: EventAction.InternalLink,
			eventName: `configurator (price bar) - click to open ${eventName}`,
			componentName: 'configurator-price-bar',
			label,
		});
	}, []);

	return (
		<CalculationInfoStyle $view={currentView} className={className}>
			{showLoadingIndicator ? (
				<Text
					tag={'p'}
					variant={{ type: 'copy', order: '1', style: 'bold' }}
					className="calculation-info-item loading">
					{i18n('ngc.currency.calculatingPrice')}...
				</Text>
			) : (
				<>
					<div className={'calculation-info total-price'} data-testid={DATA_TEST_ID.TOTAL_PRICE_CONTAINER}>
						<PriceBarTotalPrice>
							<Text
								tag={'p'}
								variant={{ type: 'copy', order: '2', style: 'normal' }}
								className="calculation-info-item">
								{i18n('ngc.priceDetails.priceAsBuilt')}
								<span style={{ cursor: 'pointer', marginLeft: '2px' }} onClick={scrollToBottom}>
									{' '}
									*{' '}
								</span>
							</Text>
							<Layout>
								<TotalPrice>{formatPriceWithVAT(totalPrice)}</TotalPrice>
							</Layout>
						</PriceBarTotalPrice>

						<TextLink
							className="text-grey"
							onClick={() => {
								openDialog('priceDetails');
								trackClickEvent(LayerType.PriceDetailsLayer, i18n('ngc.actions.showPriceDetails'));
							}}
							size={'sm'}
							variant={'standalone'}>
							{i18n('ngc.actions.showPriceDetails')}
						</TextLink>

						<PriceDetailsLegalCanadaDialog
							open={isDialogOpen('priceDetails')}
							onClose={() => {
								appTrackingService.summaryClickEvent({
									eventAction: EventAction.InternalLink,
									eventName: `configurator (${LayerType.PriceDetailsLayer}) - click to close layer`,
									componentName: 'price-details-layer',
								});
								appTrackingService.updateLayerComponent(LayerType.PriceDetailsLayer, 'close');
								appTrackingService.viewChangeEvent({
									eventName: `generic`,
									componentName: 'price-details-layer',
								});
								closeDialog('priceDetails');
							}}
						/>
					</div>

					{rate && config.enableFinancingServices && !isXlBreakpoint && (
						<div className="calculation-info monthly-price">
							<Text
								tag={'p'}
								variant={{ type: 'copy', order: '2', style: 'normal' }}
								className="calculation-info-item">
								{i18n(currentProduct === 'PL' ? 'ngc.priceDetails.lease' : 'ngc.priceDetails.finance')}
							</Text>
							<TotalPrice>{`${rate.formattedValue}`}</TotalPrice>
							<TextLink
								className="text-grey"
								onClick={() => {
									openDialog('monthlyPriceRatesFsag');
									trackClickEvent(
										LayerType.FinancingOptionsLayer,
										i18n('ngc.actions.changeLeasingRate'),
									);
								}}
								size={'sm'}
								variant={'standalone'}>
								{i18n('ngc.actions.changeLeasingRate')}
							</TextLink>
						</div>
					)}
					{cash && currentProduct === 'CA' && config.enableFinancingServices && (
						<div
							className="calculation-info monthly-price"
							data-testid={DATA_TEST_ID.CASH_FINANCE_CONTAINER}>
							<PriceBarTotalPrice>
								<Text
									tag={'p'}
									variant={{ type: 'copy', order: '2', style: 'normal' }}
									className="calculation-info-item">
									{i18n('ngc.priceDetails.cash')}
									<span style={{ cursor: 'pointer', marginLeft: '2px' }} onClick={scrollToBottom}>
										{' '}
										*{' '}
									</span>
								</Text>
								<Layout>
									<TotalPrice>{`${cash.formattedValue}`}</TotalPrice>
								</Layout>
							</PriceBarTotalPrice>

							<TextLink
								className="text-grey"
								onClick={() => {
									openDialog('monthlyPriceRatesFsag');
									trackClickEvent(
										LayerType.FinancingOptionsLayer,
										i18n('ngc.actions.changeLeasingRate'),
									);
								}}
								size={'sm'}
								variant={'standalone'}>
								{i18n('ngc.actions.changeLeasingRate')}
							</TextLink>
						</div>
					)}
					{apr && config.enableFinancingServices && !isXlBreakpoint && (
						<>
							<div
								className={'calculation-info monthly-price'}
								data-testid={DATA_TEST_ID.APR_FINANCE_CONTAINER}>
								<Text
									tag={'p'}
									variant={{ type: 'copy', order: '2', style: 'normal' }}
									className="calculation-info-item">
									{' '}
								</Text>
								<PriceBarTotalPrice>
									<TotalPrice>{`${apr.formattedValue}`}</TotalPrice>
								</PriceBarTotalPrice>
								<TextLink
									className="text-grey"
									onClick={() => openDialog('financingDisclaimer')}
									size={'sm'}
									variant={'standalone'}>
									{i18n(
										currentProduct === 'PL'
											? 'ngc.actions.leaseDisclaimer'
											: 'ngc.actions.financeDisclaimer',
									)}
								</TextLink>
							</div>
						</>
					)}
					{config.enableFinancingServices && (
						<FsagMonthlyPriceRateDialog
							open={isDialogOpen('monthlyPriceRatesFsag')}
							onClose={() => closeDialog('monthlyPriceRatesFsag')}
						/>
					)}
					{config.enableFinancingServices && (
						<FinancingDisclaimerDialog
							open={isDialogOpen('financingDisclaimer')}
							onClose={() => closeDialog('financingDisclaimer')}
						/>
					)}
				</>
			)}
		</CalculationInfoStyle>
	);
}
